import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../../Context/AuthContext";
import { fetchPDashboardData, fetchCompanyCodes } from "../../Api/projectDashboard";
import "../../Styling/ProjectDashboard.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import autoTable from "jspdf-autotable";


const ProjectDashboard = () => {
  const { user } = useAuth();
  
  const userOrgId = useMemo(() => 
    user?.organisationId?.toString() || "", 
    [user?.organisationId]
  );

  const [filters, setFilters] = useState({
    companyCode: "",
    fiscalYear: "",
    fiscalMonth: "",
  });
  
  const [companyCodes, setCompanyCodes] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [allData, setAllData] = useState([]);
  const [amountUnit, setAmountUnit] = useState("lakh"); 
// "lakh" | "crore" | "million"

  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    basicInfo: true,
    sales: true,
    salaries: true,
    expenses: true,
    vendor: true,
    revenue: true,
  });

  // Debug reference
  const debugInfoRef = useRef({
    apiParams: {},
    responseCount: 0,
    filteredCount: 0
  });

  // Memoize fiscal years and months
  const fiscalYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: 2026 - currentYear + 1 },
      (_, i) => (currentYear + i).toString()
    );
  }, []);

  const months = useMemo(() => [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ], []);

  // Load company codes
  const loadCompanyCodes = useCallback((orgId) => {
    if (!orgId) return [];
    
    if (orgId === "9001") {
      return [
        { value: "", label: "All Companies" }, 
        { value: "1000", label: "1000 - Saisatwik Technologies Private Limited" },
        { value: "2000", label: "2000 - Bizflow" }
      ];
    } else if (orgId === "5000") {
      return [
        { value: "", label: "All Companies" },
        { value: "5000", label: "5000" }
      ];
    } else {
      return [
        { value: "", label: "All Companies" },
        { value: orgId, label: `Company ${orgId}` }
      ];
    }
  }, []);

  // Main data loader
  const loadData = useCallback(async (filterParams = filters) => {
    if (!userOrgId) {
      setError("User organization ID not found");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const queryParams = {
        OrganisationID: parseInt(userOrgId),
        ...(filterParams.companyCode && { CompanyCode: parseInt(filterParams.companyCode) }),
        ...(filterParams.fiscalYear && { FiscalYear: parseInt(filterParams.fiscalYear) }),
        ...(filterParams.fiscalMonth && { FiscalMonth: parseInt(filterParams.fiscalMonth) }),
      };

      console.log("API Params:", queryParams);
      const response = await fetchPDashboardData(queryParams, user.token);
      
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (response?.data) {
        data = Array.isArray(response.data) ? response.data : [response.data];
      }

      console.log("API Response data:", data);
      
      if (!filterParams.companyCode) {
        setAllData(data);
      }
      
      let filteredData = data;
      
      if (allData.length > 0 && filterParams.companyCode) {
        filteredData = allData.filter(item => 
          item.companyCode?.toString() === filterParams.companyCode ||
          item.CompanyCode?.toString() === filterParams.companyCode
        );
      }
      
      if (filterParams.fiscalYear) {
        filteredData = filteredData.filter(item => 
          item.fiscalYear?.toString() === filterParams.fiscalYear ||
          item.FiscalYear?.toString() === filterParams.fiscalYear
        );
      }
      
      if (filterParams.fiscalMonth) {
        filteredData = filteredData.filter(item => 
          item.fiscalMonth?.toString() === filterParams.fiscalMonth ||
          item.FiscalMonth?.toString() === filterParams.fiscalMonth
        );
      }

      debugInfoRef.current = {
        apiParams: queryParams,
        responseCount: data.length,
        filteredCount: filteredData.length
      };

      setGridData(filteredData);
      
    } catch (err) {
      setError("Failed to load dashboard data: " + err.message);
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, [userOrgId, user?.token, allData]);

  // Initial load
  useEffect(() => {
    if (userOrgId) {
      const codes = loadCompanyCodes(userOrgId);
      setCompanyCodes(codes);
      loadData({
        companyCode: "",
        fiscalYear: "",
        fiscalMonth: "",
      });
    }
  }, [userOrgId, loadCompanyCodes]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      companyCode: "",
      fiscalYear: "",
      fiscalMonth: "",
    });
    if (allData.length > 0) {
      setGridData(allData);
    } else {
      loadData({
        companyCode: "",
        fiscalYear: "",
        fiscalMonth: "",
      });
    }
  };

  // Apply filters
  useEffect(() => {
    if (userOrgId) {
      const timer = setTimeout(() => {
        loadData(filters);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [filters, userOrgId]);

  // Summary calculations
  const summaryData = useMemo(() => {
    const totalExpenseDone = gridData.reduce((sum, row) => {
      const expense = row.totalExpenseDone || row.TotalExpenseDone || 0;
      return sum + (parseFloat(expense) || 0);
    }, 0);
    
    const totalExpensePending = gridData.reduce((sum, row) => {
      const expense = row.totalExpensePending || row.TotalExpensePending || 0;
      return sum + (parseFloat(expense) || 0);
    }, 0);
    
    const totalRevenueDone = gridData.reduce((sum, row) => {
      const revenue = row.totalRevenueDone || row.TotalRevenueDone || 0;
      return sum + (parseFloat(revenue) || 0);
    }, 0);
    
    const totalRevenuePending = gridData.reduce((sum, row) => {
      const revenue = row.totalRevenuePending || row.TotalRevenuePending || 0;
      return sum + (parseFloat(revenue) || 0);
    }, 0);
    
    const currentPL = gridData.reduce((sum, row) => {
      const pl = row.currentPL || row.CurrentPL || 0;
      return sum + (parseFloat(pl) || 0);
    }, 0);
    
    const expectedPL = gridData.reduce((sum, row) => {
      const pl = row.expectedPL || row.ExpectedPL || 0;
      return sum + (parseFloat(pl) || 0);
    }, 0);
    
    return {
      totalProjects: gridData.length,
      totalExpenseDone: totalExpenseDone,
      totalExpensePending: totalExpensePending,
      totalRevenueDone: totalRevenueDone,
      totalRevenuePending: totalRevenuePending,
      currentPL: currentPL,
      expectedPL: expectedPL,
    };
  }, [gridData]);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (amount === null || amount === undefined) return "₹ 0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);
  
  const convertAmountByUnit = useCallback((amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return "-";

    let converted = amount;
    let suffix = "";

    switch (amountUnit) {
      case "lakh":
        converted = amount / 100000;
        suffix = " L";
        break;

      case "million":
        converted = amount / 1000000;
        suffix = " M";
        break;

      case "crore":
      default:
        converted = amount / 10000000;
        suffix = " Cr";
        break;
    }

    return `${converted.toFixed(2)}${suffix}`;
  }, [amountUnit]);


  // Format hours
  const formatHours = useCallback((hours) => {
    if (hours === null || hours === undefined) return "-";
    return `${hours} hrs`;
  }, []);

  // Get value from row with fallbacks
  const getValue = useCallback((row, ...keys) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) return row[key];
    }
    return null;
  }, []);

  // Column configuration
  const columns = useMemo(() => [
    // Basic Info
    {
      id: 'basicInfo',
      label: 'Basic Information',
      show: visibleColumns.basicInfo,
      fields: [
        { key: 'customerCode', header: 'Client Code', width: '100px', formatter: (val) => val || '-' },
        { key: 'customerName', header: 'Client Name', width: '150px', formatter: (val) => val || '-' },
        { key: 'projectCode', header: 'Project Code', width: '120px', formatter: (val) => val || '-' },
        { key: 'projectName', header: 'Project Name', width: '200px', formatter: (val) => val || '-' },
      ]
    },
    // Sales/Invoicing
    {
      id: 'sales',
      label: 'Sales & Invoicing',
      show: visibleColumns.sales,
      fields: [
        { key: 'poAmount', header: 'PO Amount', width: '120px', formatter: (val) => convertAmountByUnit(val)
 },
        { key: 'amountDueForBilling', header: 'Amount Due for Billing', width: '150px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'invoicedAmount', header: 'Invoiced Amount', width: '130px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'salesOrderAmount', header: 'Sales Order Amount', width: '140px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'salesInvoicesPaid', header: 'Sales Invoices Paid', width: '150px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'salesInvoicePendingForPosting', header: 'Sales Invoice Pending', width: '160px', formatter: (val) => convertAmountByUnit(val) },
      ]
    },
    // Salaries
    {
      id: 'salaries',
      label: 'Salaries',
      show: visibleColumns.salaries,
      fields: [
        { key: 'salariesBooked', header: 'Salaries Booked', width: '130px',formatter: (val) => convertAmountByUnit(val)
 },
        { key: 'salariesPaid', header: 'Salaries Paid', width: '120px',formatter: (val) => convertAmountByUnit(val)
},
        { key: 'salariesPending', header: 'Salaries Pending', width: '130px',formatter: (val) => convertAmountByUnit(val)},
      ]
    },
    // Expenses
    {
      id: 'expenses',
      label: 'Expenses',
      show: visibleColumns.expenses,
      fields: [
        { key: 'submittedExpenses', header: 'Submitted Expenses', width: '140px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'approvedExpenses', header: 'Approved Expenses', width: '140px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'paidExpenses', header: 'Paid Expenses', width: '120px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'unpaidExpenses', header: 'Unpaid Expenses', width: '130px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'timeCardNonBillableHours', header: 'TC Non-Billable Hours', width: '160px', formatter: formatHours },
        { key: 'timeCardBillableHours', header: 'TC Billable Hours', width: '150px', formatter: formatHours },
      ]
    },
    // Vendor & Freelancer
    {
      id: 'vendor',
      label: 'Vendor & Freelancer',
      show: visibleColumns.vendor,
      fields: [
        { key: 'outgoingPOVendor', header: 'Outgoing PO Vendor', width: '150px',formatter: (val) => convertAmountByUnit(val) },
        { key: 'outgoingPOFreelancer', header: 'Outgoing PO Freelancer', width: '170px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'vendorInvoices', header: 'Vendor Invoices', width: '130px', formatter: (val) => convertAmountByUnit(val)},
        { key: 'freelancerInvoices', header: 'Freelancer Invoices', width: '150px',formatter: (val) => convertAmountByUnit(val) },
        { key: 'vendorPayments', header: 'Vendor Payments', width: '130px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'freelancerPayments', header: 'Freelancer Payments', width: '150px', formatter: (val) => convertAmountByUnit(val)  },
        { key: 'vendorPaymentPending', header: 'Vendor Payment Pending', width: '160px',  formatter: (val) => convertAmountByUnit(val)  },
        { key: 'freelancerPaymentPending', header: 'Freelancer Payment Pending', width: '170px',  formatter: (val) => convertAmountByUnit(val) },
      ]
    },
    // Revenue & P&L
    {
      id: 'revenue',
      label: 'Revenue & P&L',
      show: visibleColumns.revenue,
      fields: [
        { key: 'revenue', header: 'Revenue', width: '120px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'totalExpenseDone', header: 'Total Expense Done', width: '150px',  formatter: (val) => convertAmountByUnit(val)  },
        { key: 'totalExpensePending', header: 'Total Expense Pending', width: '160px', formatter: (val) => convertAmountByUnit(val)  },
        { key: 'totalRevenueDone', header: 'Total Revenue Done', width: '150px', formatter: (val) => convertAmountByUnit(val) },
        { key: 'totalRevenuePending', header: 'Total Revenue Pending', width: '160px', formatter: (val) => convertAmountByUnit(val)  },
        { key: 'currentPL', header: 'Current P&L', width: '120px',  formatter: (val) => convertAmountByUnit(val) , 
          className: (val) => val >= 0 ? 'positive' : 'negative' },
        { key: 'expectedPL', header: 'Expected P&L', width: '130px',  formatter: (val) => convertAmountByUnit(val) ,
          className: (val) => val >= 0 ? 'positive' : 'negative' },
      ]
    },
  ], [visibleColumns, formatCurrency, formatHours, convertAmountByUnit, amountUnit]);

  // Improved Excel Export


const handleExportExcel = useCallback(() => {
  if (gridData.length === 0) {
    alert("No data to export!");
    return;
  }

  const rows = [];

  gridData.forEach(row => {
    const excelRow = {};

    columns
      .filter(c => c.show)
      .forEach(col => {
        col.fields.forEach(field => {
          const value = getValue(row, field.key);

          excelRow[field.header] =
            typeof value === "number"
              ? value
              : value ?? "";
        });
      });

    rows.push(excelRow);
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Project Dashboard");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(
    blob,
    `Project_Dashboard_${userOrgId}_${new Date()
      .toISOString()
      .split("T")[0]}.xlsx`
  );
}, [gridData, columns, getValue, userOrgId]);


  // PDF Export Function

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const handleExportPDF = useCallback(() => {
  if (!gridData.length) {
    alert("No data to export!");
    return;
  }

  const pdf = new jsPDF("l", "pt", "a4");

  pdf.setFontSize(14);
  pdf.text("Project Dashboard Report", 40, 30);

  // -----------------------------
  // Build all headers & rows
  // -----------------------------
  const allHeaders = [];
  const allRows = [];

  columns
    .filter(c => c.show)
    .forEach(col => {
      col.fields.forEach(field => {
        allHeaders.push({
          header: field.header,
          key: field.key,
        });
      });
    });

  gridData.forEach(row => {
    const rowObj = {};
    allHeaders.forEach(h => {
      const value = getValue(row, h.key);
      rowObj[h.header] =
        typeof value === "number"
          ? value.toLocaleString("en-IN")
          : value ?? "-";
    });
    allRows.push(rowObj);
  });

  // -----------------------------
  // 🔥 Split columns (IMPORTANT)
  // -----------------------------
  const COLUMN_PER_PAGE = 8; // NEVER increase above 8
  const headerChunks = chunkArray(allHeaders, COLUMN_PER_PAGE);

  let isFirstTable = true;

  headerChunks.forEach(chunk => {
    if (!isFirstTable) pdf.addPage();
    isFirstTable = false;

    const headers = chunk.map(c => c.header);
    const body = allRows.map(row =>
      chunk.map(c => row[c.header])
    );

    autoTable(pdf, {
      head: [headers],
      body,
      startY: 50,
      theme: "grid",

      styles: {
        fontSize: 7,
        cellPadding: 4,
        overflow: "linebreak",
        valign: "middle",
        halign: "right",
      },

      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 8,
        halign: "center",
      },

      margin: { left: 20, right: 20 },

      didDrawPage: () => {
        pdf.setFontSize(10);
        pdf.text("Project Dashboard Report", 40, 30);
        pdf.setFontSize(8);
        pdf.text(
          `Page ${pdf.internal.getNumberOfPages()}`,
          pdf.internal.pageSize.getWidth() - 60,
          pdf.internal.pageSize.getHeight() - 20
        );
      },
    });
  });

  pdf.save(
    `Project_Dashboard_${userOrgId}_${new Date()
      .toISOString()
      .split("T")[0]}.pdf`
  );
}, [gridData, columns, getValue, userOrgId]);




  // Toggle column visibility
  const toggleColumn = useCallback((columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  }, []);

  return (
    <div className="pd-container">
      {/* Header Section */}
      <div className="pd-header">
        <div className="pd-header__left">
          <h1 className="pd-header__title">Project Dashboard</h1>
          <p className="pd-header__subtitle">Comprehensive project financial tracking</p>
          <div className="pd-user-org">
            <span className="pd-user-org__label">Organization ID:</span>
            <span className="pd-user-org__value">{userOrgId}</span>
          </div>
        </div>
        <div className="pd-header__actions">
          <button 
            className="pd-btn pd-btn--secondary"
            onClick={() => loadData(filters)}
            disabled={loading}
          >
            <span className="pd-btn__icon">↻</span> Refresh
          </button>
          <button 
            className="pd-btn pd-btn--excel"
            onClick={handleExportExcel}
            disabled={gridData.length === 0 || loading}
          >
            <span className="pd-btn__icon">📊</span> Excel
          </button>
          <button 
            className="pd-btn pd-btn--pdf"
            onClick={handleExportPDF}
            disabled={gridData.length === 0 || loading || pdfGenerating}
          >
            <span className="pd-btn__icon">📄</span> 
            {pdfGenerating ? 'Generating...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="pd-summary-grid">
        {/* Total Projects Card (Remains same) */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#e3f2fd' }}>
            📊
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Projects</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : summaryData.totalProjects}
            </p>
          </div>
        </div>
        
        {/* Total Expense Done */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#ffebee' }}>
            💸
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Expense Done</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : formatCurrency(summaryData.totalExpenseDone)}
            </p>
          </div>
        </div>
        
        {/* Total Expense Pending */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#fff3e0' }}>
            ⏳
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Expense Pending</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : formatCurrency(summaryData.totalExpensePending)}
            </p>
          </div>
        </div>
        
        {/* Total Revenue Done */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#e8f5e9' }}>
            💰
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Revenue Done</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : formatCurrency(summaryData.totalRevenueDone)}
            </p>
          </div>
        </div>

        {/* Total Revenue Pending */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#f3e5f5' }}>
            📋
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Revenue Pending</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : formatCurrency(summaryData.totalRevenuePending)}
            </p>
          </div>
        </div>

        {/* Current P&L */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#e1f5fe' }}>
            📈
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Current P&L</h3>
            <p className={`pd-summary-card__value ${summaryData.currentPL >= 0 ? 'positive' : 'negative'}`}>
              {loading ? "..." : formatCurrency(summaryData.currentPL)}
            </p>
          </div>
        </div>

        {/* Expected P&L */}
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#fce4ec' }}>
            🎯
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Expected P&L</h3>
            <p className={`pd-summary-card__value ${summaryData.expectedPL >= 0 ? 'positive' : 'negative'}`}>
              {loading ? "..." : formatCurrency(summaryData.expectedPL)}
            </p>
          </div>
          
        </div>
      </div>

      {/* Filters Section */}
      <div className="pd-filters-card">
        <div className="pd-filters-card__header">
          <h3 className="pd-filters-card__title">Filters</h3>
          <div className="pd-filters-actions">
            <button 
              className="pd-btn-text"
              onClick={handleResetFilters}
              disabled={loading}
            >
              Clear Filters
            </button>
          </div>
        </div>
        
        <div className="pd-filters-grid">
          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Company Code</label>
            <select
              className="pd-filter-group__select"
              name="companyCode"
              value={filters.companyCode}
              onChange={handleFilterChange}
              disabled={loading || companyCodes.length === 0}
            >
              {companyCodes.length === 0 ? (
                <option value="">Loading...</option>
              ) : (
                companyCodes.map((code) => (
                  <option key={code.value} value={code.value}>
                    {code.label}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Fiscal Year</label>
            <select
              className="pd-filter-group__select"
              name="fiscalYear"
              value={filters.fiscalYear}
              onChange={handleFilterChange}
              disabled={loading}
            >
              <option value="">All Years</option>
              {fiscalYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Month</label>
            <select
              className="pd-filter-group__select"
              name="fiscalMonth"
              value={filters.fiscalMonth}
              onChange={handleFilterChange}
              disabled={loading}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Column Toggles */}
      <div className="pd-column-toggle">
        <h4 className="pd-column-toggle__title">Show/Hide Columns:</h4>
        <div className="pd-column-toggle__buttons">
          {columns.map(col => (
            <button
              key={col.id}
              className={`pd-column-toggle__btn ${col.show ? 'active' : ''}`}
              onClick={() => toggleColumn(col.id)}
            >
              {col.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="pd-loading-container">
          <div className="pd-loading-bar"></div>
          <div className="pd-loading-text">Loading data...</div>
        </div>
      )}

      {/* Data Grid */}
      {!loading && gridData.length > 0 && (
        <div className="pd-data-grid-container">
          <div className="pd-data-grid__header">
             <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <span style={{ fontSize: "13px", fontWeight: 500 }}>Amount Unit:</span>

    <select
      value={amountUnit}
      onChange={(e) => setAmountUnit(e.target.value)}
      className="pd-filter-group__select"
      style={{ width: "120px" }}
    >
      <option value="crore">In Crore</option>
      <option value="lakh">In Lakhs</option>
      <option value="million">In Million</option>
    </select>
  </div>
            <h3 className="pd-data-grid__title">Project Financial Details</h3>
            <span className="pd-data-grid__record-count">
              {gridData.length} records • {columns.filter(c => c.show).flatMap(c => c.fields).length} columns shown
            </span>
          </div>
          
          <div className="pd-table-wrapper">
            <table className="pd-data-grid">
              <thead>
                <tr className="pd-data-grid__header-row">
                  {/* Basic Info Columns */}
                  {columns.find(c => c.id === 'basicInfo')?.show && 
                    columns.find(c => c.id === 'basicInfo').fields.map(field => (
                      <th 
                        key={field.key}
                        className="pd-data-grid__header-cell"
                        style={{ width: field.width }}
                      >
                        {field.header}
                      </th>
                    ))
                  }
                  
                  {/* Other Columns */}
                  {columns.filter(c => c.id !== 'basicInfo' && c.show).map(col => 
                    col.fields.map(field => (
                      <th 
                        key={`${col.id}-${field.key}`}
                        className="pd-data-grid__header-cell"
                        style={{ width: field.width }}
                      >
                        {field.header}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {gridData.map((row, index) => (
                  <tr key={`${getValue(row, 'projectCode')}-${index}`} 
                      className="pd-data-grid__body-row">
                    
                    {/* Basic Info Cells */}
                    {columns.find(c => c.id === 'basicInfo')?.show && 
                      columns.find(c => c.id === 'basicInfo').fields.map(field => (
                        <td key={field.key} className="pd-data-grid__body-cell">
                          <span className={`pd-data-cell pd-data-cell--${field.key}`}>
                            {field.formatter(getValue(row, field.key))}
                          </span>
                        </td>
                      ))
                    }
                    
                    {/* Other Cells */}
                    {columns.filter(c => c.id !== 'basicInfo' && c.show).map(col => 
                      col.fields.map(field => {
                        const value = getValue(row, field.key);
                        const cellClassName = field.className ? 
                          typeof field.className === 'function' ? 
                            field.className(value) : field.className 
                          : '';
                        
                        return (
                          <td key={`${col.id}-${field.key}`} className="pd-data-grid__body-cell">
                            <span className={`pd-data-cell pd-data-cell--amount ${cellClassName}`}>
                              {field.formatter ? field.formatter(value) : value || '-'}
                            </span>
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && gridData.length === 0 && (
        <div className="pd-empty-state">
          <div className="pd-empty-state__icon">📊</div>
          <h3 className="pd-empty-state__title">No data available</h3>
          <p className="pd-empty-state__description">
            {filters.companyCode 
              ? `No projects found for company code ${filters.companyCode}. Try adjusting your filters.`
              : "No projects found. Try adjusting your filters or check back later."}
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="pd-error-state">
          <div className="pd-error-state__icon">⚠️</div>
          <h3 className="pd-error-state__title">Error</h3>
          <p className="pd-error-state__description">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard;